import { Component } from '@angular/core';
import MiniSearch from 'minisearch';
import type { SearchResult } from 'minisearch';

import { FeatureTourService } from '../../main/shared/tour-step-template/tours/feature-tour.service';
import { TourScenarioService } from '../../main/shared/tour-step-template/tour-scenario.service';
import { FeatureTourStep, TourScenarioRecord } from '../../main/shared/tour-step-template/tours/tour.types';

type StepFreshness = 'fresh' | 'recent' | 'warming';

interface StepFreshnessMeta {
  freshness?: StepFreshness;
  label?: string;
  badgeText?: string;
}

interface TableOfContentsChapter {
  id: string;
  feature: string;
  title: string;
  description?: string;
  sections: TableOfContentsSection[];
  expanded: boolean;
  showAllSections: boolean;
  order?: number;
  latestCreatedAt?: number;
  originalIndex: number;
  freshnessBadgeVariant?: 'allFresh' | 'containsFresh';
  freshnessBadgeText?: string;
  freshnessBadgeLabel?: string;
}

interface TableOfContentsSection {
  title: string;
  stepIndex: number;
  target: number | string;
  description?: string;
  createdAt?: string;
  freshness?: StepFreshness;
  freshnessLabel?: string;
  freshnessBadgeText?: string;
}

interface TableOfContentsSearchDocument {
  id: string;
  scenarioId: string;
  feature: string;
  scenarioTitle: string;
  scenarioLabel: string;
  stepTitle: string;
  content: string;
  keywords: string;
  stepIndex: number;
  target: number | string;
  ngrams: string;
  freshness?: StepFreshness;
  freshnessLabel?: string;
  freshnessBadgeText?: string;
}

interface TableOfContentsSearchResult {
  scenarioId: string;
  feature: string;
  scenarioTitle: string;
  scenarioLabel: string;
  stepTitle: string;
  stepIndex: number;
  target: number | string;
  snippet: string;
  score: number;
  ngrams?: string;
  freshness?: StepFreshness;
  freshnessLabel?: string;
  freshnessBadgeText?: string;
}

@Component({
  selector: 'app-floating-toc',
  templateUrl: './floating-toc.component.html',
  styleUrls: ['./floating-toc.component.scss'],
  standalone: false,
})
export class FloatingTocComponent {
  isOpen = false;
  allExpanded = true;
  sortNewestFirst = false;
  searchQuery = '';
  searchResults: TableOfContentsSearchResult[] = [];

  chapters: TableOfContentsChapter[] = [];
  globalFreshnessIndicatorVariant?: 'allFresh' | 'containsFresh';
  globalFreshnessIndicatorLabel?: string;
  private readonly scenarioLookup = new Map<string, TourScenarioRecord>();
  private miniSearch?: MiniSearch<TableOfContentsSearchDocument>;
  private readonly sectionPreviewLimit = 6;
  private readonly dayInMs = 24 * 60 * 60 * 1000;
  private readonly createdAtFormatter = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' });

  constructor(
    private readonly tourScenarioService: TourScenarioService,
    private readonly featureTourService: FeatureTourService,
  ) {
    const scenarios = this.tourScenarioService.getAll();
    scenarios.forEach((scenario) => this.scenarioLookup.set(scenario.id, scenario));
    this.chapters = this.buildChapters(scenarios);
    const hasFreshChapters = this.chapters.some((chapter) => typeof chapter.freshnessBadgeVariant === 'string');
    if (hasFreshChapters) {
      this.sortNewestFirst = true;
    }
    this.applyChapterSorting();
    this.miniSearch = this.buildSearchIndex(scenarios);
  }

  private buildChapters(items: TourScenarioRecord[]): TableOfContentsChapter[] {
    return items.map((item, index) => {
      const chapterDescription = this.extractScenarioDescription(item);
      const latestCreatedAt = this.getLatestCreatedAtTimestamp(item.steps);
      const sections = item.steps.map((step, stepIndex) => {
        const sectionDescriptionSource = this.getStepContentPlainText(step);
        return {
          title: step.title ?? step.anchorId ?? `Step ${stepIndex + 1}`,
          stepIndex,
          target: (step.stepId ?? stepIndex) as number | string,
          description: this.buildPreview(sectionDescriptionSource, 160),
          ...this.buildSectionFreshness(step),
        };
      });
      const chapterFreshness = this.buildChapterFreshnessMeta(sections);

      return {
        id: item.id,
        feature: item.feature,
        title: item.label ?? item.title,
        description: this.buildPreview(chapterDescription),
        sections,
        expanded: false,
        showAllSections: false,
        order: item.order,
        latestCreatedAt,
        originalIndex: index,
        freshnessBadgeVariant: chapterFreshness?.variant,
        freshnessBadgeText: chapterFreshness?.text,
        freshnessBadgeLabel: chapterFreshness?.label,
      };
    });
  }

  private applyChapterSorting(): void {
    const comparator = this.sortNewestFirst
      ? (a: TableOfContentsChapter, b: TableOfContentsChapter) => this.compareChaptersByNewest(a, b)
      : (a: TableOfContentsChapter, b: TableOfContentsChapter) => this.compareChaptersByOrder(a, b);

    const sorted = [...this.chapters].sort(comparator);
    this.chapters = sorted;
    this.updateAllExpandedState();
    this.updateGlobalFreshnessIndicator();
  }

  private compareChaptersByOrder(a: TableOfContentsChapter, b: TableOfContentsChapter): number {
    const aHasOrder = typeof a.order === 'number';
    const bHasOrder = typeof b.order === 'number';

    if (aHasOrder && bHasOrder && a.order !== b.order) {
      return (a.order as number) - (b.order as number);
    }

    if (aHasOrder && !bHasOrder) {
      return -1;
    }

    if (!aHasOrder && bHasOrder) {
      return 1;
    }

    return a.originalIndex - b.originalIndex;
  }

  private compareChaptersByNewest(a: TableOfContentsChapter, b: TableOfContentsChapter): number {
    const aHasDate = typeof a.latestCreatedAt === 'number';
    const bHasDate = typeof b.latestCreatedAt === 'number';

    if (aHasDate && bHasDate && a.latestCreatedAt !== b.latestCreatedAt) {
      return (b.latestCreatedAt as number) - (a.latestCreatedAt as number);
    }

    if (aHasDate && !bHasDate) {
      return -1;
    }

    if (!aHasDate && bHasDate) {
      return 1;
    }

    return this.compareChaptersByOrder(a, b);
  }

  private updateAllExpandedState(): void {
    this.allExpanded = this.chapters.length ? this.chapters.every((chapter) => chapter.expanded) : true;
  }

  private updateGlobalFreshnessIndicator(): void {
    const variants = this.chapters
      .map((chapter) => chapter.freshnessBadgeVariant)
      .filter((variant): variant is 'allFresh' | 'containsFresh' => typeof variant === 'string');

    if (!variants.length) {
      this.globalFreshnessIndicatorVariant = undefined;
      this.globalFreshnessIndicatorLabel = undefined;
      return;
    }

    const hasFresh = variants.includes('allFresh');
    if (hasFresh) {
      this.globalFreshnessIndicatorVariant = 'allFresh';
      this.globalFreshnessIndicatorLabel = 'At least one quick guide chapter was added recently.';
      return;
    }

    const hasUpdates = variants.includes('containsFresh');
    if (hasUpdates) {
      this.globalFreshnessIndicatorVariant = 'containsFresh';
      this.globalFreshnessIndicatorLabel = 'Some quick guide chapters were updated recently.';
      return;
    }

    this.globalFreshnessIndicatorVariant = undefined;
    this.globalFreshnessIndicatorLabel = undefined;
  }

  get hasSearchQuery(): boolean {
    return this.searchQuery.trim().length > 0;
  }

  toggleOpen(): void {
    this.isOpen = !this.isOpen;
  }

  toggleAll(): void {
    this.allExpanded = !this.allExpanded;
    this.chapters.forEach((chapter) => {
      chapter.expanded = this.allExpanded;
      if (!this.allExpanded) {
        chapter.showAllSections = false;
      }
    });
  }

  onSortNewestFirstToggle(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.sortNewestFirst = !!target?.checked;
    this.applyChapterSorting();
  }

  onChapterHeaderClick(event: Event, chapter: TableOfContentsChapter): void {
    this.toggleChapter(chapter);
    this.deferBlur(event);
  }

  onChapterDescriptionClick(event: Event, chapter: TableOfContentsChapter): void {
    this.toggleChapter(chapter);
    this.deferBlur(event);
  }

  toggleChapter(chapter: TableOfContentsChapter): void {
    chapter.expanded = !chapter.expanded;
    if (!chapter.expanded) {
      chapter.showAllSections = false;
    }
    this.allExpanded = this.chapters.every((item) => item.expanded);
  }

  onSearchChange(value: string): void {
    this.searchQuery = value;
    const trimmed = value.trim();

    if (!trimmed) {
      this.searchResults = [];
      return;
    }

    if (!this.miniSearch) {
      this.searchResults = [];
      return;
    }

    const hits = this.miniSearch
      .search(trimmed, {
        prefix: true,
        fuzzy: 0.2,
        combineWith: 'AND',
      })
      .slice(0, 25) as SearchResult[];

    this.searchResults = hits.map((hit) => this.toSearchResult(hit, trimmed));
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.searchResults = [];
  }

  onSectionButtonClick(event: Event, chapter: TableOfContentsChapter, section: TableOfContentsSection): void {
    this.startChapterStep(chapter, section);
    this.deferBlur(event);
  }

  onSectionDescriptionClick(event: Event, chapter: TableOfContentsChapter, section: TableOfContentsSection): void {
    this.startChapterStep(chapter, section);
    this.deferBlur(event);
  }

  startChapterStep(chapter: TableOfContentsChapter, section: TableOfContentsSection): void {
    this.launchScenario(chapter.id, section.target);
  }

  toggleChapterSections(chapter: TableOfContentsChapter): void {
    chapter.showAllSections = !chapter.showAllSections;
  }

  hasSectionOverflow(chapter: TableOfContentsChapter): boolean {
    return chapter.sections.length > this.sectionPreviewLimit;
  }

  startSearchResult(event: Event, result: TableOfContentsSearchResult): void {
    this.launchScenario(result.scenarioId, result.target);
    this.deferBlur(event);
  }

  highlight(text?: string | null): string {
    if (typeof text !== 'string' || !text.length) {
      return '';
    }

    return this.buildHighlightedHtml(text);
  }

  trackSearchResult(index: number, result: TableOfContentsSearchResult): string {
    return `${result.scenarioId}::${result.target}`;
  }

  private launchScenario(scenarioId: string, target: number | string): void {
    if (!this.scenarioLookup.has(scenarioId)) {
      return;
    }

    this.isOpen = false;
    this.featureTourService.startScenarioById(scenarioId, target);
  }

  private buildSectionFreshness(
    step: FeatureTourStep,
  ): Pick<TableOfContentsSection, 'createdAt' | 'freshness' | 'freshnessLabel' | 'freshnessBadgeText'> {
    const meta = this.evaluateStepFreshness(step.createdAt);
    return {
      createdAt: step.createdAt,
      freshness: meta.freshness,
      freshnessLabel: meta.label,
      freshnessBadgeText: meta.badgeText,
    };
  }

  private buildChapterFreshnessMeta(
    sections: TableOfContentsSection[],
  ): { variant: 'allFresh' | 'containsFresh'; text: string; label: string } | undefined {
    const totalSections = sections.length;
    if (!totalSections) {
      return undefined;
    }

    const sectionsWithFreshness = sections.filter((section) => Boolean(section.freshness));
    if (!sectionsWithFreshness.length) {
      return undefined;
    }

    const allSectionsHaveFreshness = sectionsWithFreshness.length === totalSections;
    const allFresh = allSectionsHaveFreshness && sectionsWithFreshness.every((section) => section.freshness === 'fresh');

    if (allFresh) {
      return {
        variant: 'allFresh',
        text: 'NEW',
        label: 'All walkthrough steps were added recently.',
      };
    }

    return {
      variant: 'containsFresh',
      text: 'UPDATED',
      label: 'Some walkthrough steps were added recently.',
    };
  }

  private getLatestCreatedAtTimestamp(steps: FeatureTourStep[]): number | undefined {
    const timestamps = steps
      .map((step) => step.createdAt)
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
      .map((value) => {
        const parsed = new Date(value).getTime();
        return Number.isNaN(parsed) ? undefined : parsed;
      })
      .filter((value): value is number => typeof value === 'number');

    if (!timestamps.length) {
      return undefined;
    }

    return Math.max(...timestamps);
  }

  private evaluateStepFreshness(createdAt?: string): StepFreshnessMeta {
    if (!createdAt) {
      return {};
    }

    const createdDate = new Date(createdAt);
    if (Number.isNaN(createdDate.getTime())) {
      return {};
    }

    const diffDays = (Date.now() - createdDate.getTime()) / this.dayInMs;
    const formattedDate = this.createdAtFormatter.format(createdDate);
    const suffix = `Added on ${formattedDate}.`;

    if (diffDays < 14) {
      return {
        freshness: 'fresh',
        label: `New walkthrough step · ${suffix}`,
        badgeText: 'NEW',
      };
    }

    if (diffDays < 30) {
      return {
        freshness: 'recent',
        label: `Recently added step · ${suffix}`,
        badgeText: 'RECENT',
      };
    }

    if (diffDays < 60) {
      return {
        freshness: 'warming',
        label: `Updated step · ${suffix}`,
        badgeText: 'UPDATED',
      };
    }

    return {};
  }

  private buildSearchIndex(scenarios: TourScenarioRecord[]): MiniSearch<TableOfContentsSearchDocument> {
    const documents = scenarios.flatMap((scenario) =>
      scenario.steps.map((step, index) => this.toSearchDocument(scenario, step, index))
    );

    const miniSearch = new MiniSearch<TableOfContentsSearchDocument>({
      fields: ['scenarioTitle', 'feature', 'stepTitle', 'content', 'keywords', 'ngrams'],
      storeFields: [
        'scenarioId',
        'scenarioTitle',
        'scenarioLabel',
        'feature',
        'stepTitle',
        'stepIndex',
        'target',
        'content',
        'keywords',
        'freshness',
        'freshnessLabel',
        'freshnessBadgeText',
      ],
      processTerm: (term) => this.normalize(term),
      searchOptions: {
        processTerm: (term) => this.normalize(term),
        prefix:  (term) => term.length >= 2,
        fuzzy:  (term) => (term.length > 4 ? 0.2 : false),
        boost: {
          stepTitle: 5,
          scenarioTitle: 3,
          feature: 2,
          keywords: 2.5,
          content: 1,
          ngrams: 0.35,
        },
        weights: {
          prefix: 1.6,
          fuzzy: 0.6,
        },
        combineWith: 'AND',
      },
    });

    miniSearch.addAll(documents);
    return miniSearch;
  }

  private toSearchDocument(
    scenario: TourScenarioRecord,
    step: FeatureTourStep,
    index: number,
  ): TableOfContentsSearchDocument {
    const stepTitle = step.title ?? step.anchorId ?? `Step ${index + 1}`;
    const contentPlain = this.getStepContentPlainText(step);
    const keywordText = this.buildKeywordText(scenario, step);
    const sectionMeta = this.buildSectionFreshness(step);

    return {
      id: `${scenario.id}::${index}`,
      scenarioId: scenario.id,
      feature: scenario.feature,
      scenarioTitle: scenario.title,
      scenarioLabel: scenario.label ?? scenario.title,
      stepTitle,
      content: contentPlain,
      keywords: keywordText,
      stepIndex: index,
      target: (step.stepId ?? index) as number | string,
      ngrams: this.buildNgramsField(
        scenario.title,
        scenario.label ?? '',
        scenario.feature,
        scenario.tags.join(' '),
        scenario.keywords.join(' '),
        stepTitle,
        contentPlain,
        keywordText
      ),
      freshness: sectionMeta.freshness,
      freshnessLabel: sectionMeta.freshnessLabel,
      freshnessBadgeText: sectionMeta.freshnessBadgeText,
    };
  }

  private toSearchResult(hit: SearchResult, query: string): TableOfContentsSearchResult {
    const scenarioId = hit.scenarioId as string;
    const feature = hit.feature as string;
    const scenarioTitle = hit.scenarioTitle as string;
    const scenarioLabel = hit.scenarioLabel as string | undefined;
    const stepTitle = hit.stepTitle as string;
    const stepIndex = Number(hit.stepIndex ?? 0);
    const target = (hit.target as number | string) ?? stepIndex;
    const content = typeof hit.content === 'string' ? hit.content : '';
    const keywords = typeof hit.keywords === 'string' ? hit.keywords : '';
    const freshness = (hit.freshness as StepFreshness | undefined) ?? undefined;
    const freshnessLabel = typeof hit.freshnessLabel === 'string' ? hit.freshnessLabel : undefined;
    const freshnessBadgeText = typeof hit.freshnessBadgeText === 'string' ? hit.freshnessBadgeText : undefined;

    return {
      scenarioId,
      feature,
      scenarioTitle,
      scenarioLabel: scenarioLabel ?? scenarioTitle,
      stepTitle,
      stepIndex,
      target,
      snippet: this.buildSnippet([content, keywords].filter(Boolean).join(' '), query),
      score: hit.score,
      freshness,
      freshnessLabel,
      freshnessBadgeText,
    };
  }

  private buildSnippet(content: string, query: string): string {
    if (!content) {
      return '';
    }

    const normalizedContent = content.trim();
    const terms = query
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean);

    const lowerContent = normalizedContent.toLowerCase();
    const firstMatch = terms
      .map((term) => lowerContent.indexOf(term))
      .filter((index) => index >= 0)
      .sort((a, b) => a - b)[0] ?? 0;

    const start = Math.max(0, firstMatch - 40);
    const end = Math.min(normalizedContent.length, firstMatch + 120);
    const snippet = normalizedContent.slice(start, end).trim();

    return end < normalizedContent.length ? `${snippet}…` : snippet;
  }

  private stripHtml(value: string): string {
    return value
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /** Normalizacja: małe litery + zdjęcie znaków diakrytycznych (PL, itp.) */
  private normalize(text: string): string {
    return text
      .toLocaleLowerCase('pl')
      .normalize('NFD')
      .replace(/\p{M}/gu, ''); // usuń znaki łączące (akcenty/ogonki)
  }

  /** Prosta tokenizacja do n‑gramów: po whitespace/punkcji, bez pustych */
  private tokens(text: string): string[] {
    return this.normalize(text)
      .replace(/[^a-z0-9]+/gu, ' ')
      .split(/\s+/)
      .filter(Boolean);
  }

  /** Zwraca n‑gramy przesuwne danego tokenu, np. token="przyklad" → "prz","rzy","zyk",...  */
  private ngramsFor(token: string, min = 3, max = 12): string[] {
    const grams: string[] = [];
    const upper = Math.min(max, token.length);
    for (let n = min; n <= upper; n++) {
      for (let i = 0; i + n <= token.length; i++) {
        grams.push(token.slice(i, i + n));
      }
    }
    return grams;
  }

  /** Buduje zawartość pola `ngrams` na podstawie kilku części tekstu */
  private buildNgramsField(...parts: string[]): string {
    const set = new Set<string>();
    for (const part of parts) {
      for (const tok of this.tokens(part)) {
        for (const g of this.ngramsFor(tok)) {
          set.add(g);
        }
      }
    }
    // MiniSearch i tak potokenizuje po spacji; chcemy, by każdy n‑gram był „terminem”
    return Array.from(set).join(' ');
  }

  private buildKeywordText(scenario: TourScenarioRecord, step: FeatureTourStep): string {
    return [
      ...scenario.tags,
      ...scenario.keywords,
      ...(step.keywords ?? []),
    ].join(' ');
  }

  private buildPreview(text: string, limit = 180): string | undefined {
    const trimmed = text.trim();
    if (!trimmed) {
      return undefined;
    }
    if (trimmed.length <= limit) {
      return trimmed;
    }
    return `${trimmed.slice(0, limit - 1).trimEnd()}…`;
  }

  private getStepContentPlainText(step: FeatureTourStep): string {
    const parts: Array<string | undefined> = [];

    if (step.richContent) {
      parts.push(
        step.richContent.shortDescription,
        step.richContent.longDescription,
        step.richContent.impactOnSystem,
        step.richContent.risks,
      );
    }

    if (typeof step.content === 'string') {
      parts.unshift(step.content);
    }

    const combined = parts
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
      .join('\n\n');

    return this.stripHtml(combined);
  }

  private extractScenarioDescription(item: TourScenarioRecord): string {
    if (item.description) {
      return this.stripHtml(item.description);
    }

    const firstStepWithContent = item.steps.find((step) => this.getStepContentPlainText(step).length > 0);
    if (firstStepWithContent) {
      return this.getStepContentPlainText(firstStepWithContent);
    }

    const firstStepWithTitle = item.steps.find((step) => step.title);
    if (firstStepWithTitle?.title) {
      return firstStepWithTitle.title;
    }

    return '';
  }

  private buildHighlightedHtml(text: string): string {
    const trimmedQuery = this.searchQuery.trim();
    if (!trimmedQuery) {
      return this.escapeHtml(text);
    }

    const terms = trimmedQuery
      .split(/\s+/)
      .map((term) => term.trim())
      .filter(Boolean)
      .map((term) => this.normalize(term))
      .filter((term) => term.length > 0);

    if (!terms.length) {
      return this.escapeHtml(text);
    }

    const rawChars = Array.from(text);
    if (!rawChars.length) {
      return '';
    }

    const normalizedSegments = rawChars.map((char) => this.normalize(char));
    const normalizedText = normalizedSegments.join('');
    if (!normalizedText.length) {
      return this.escapeHtml(text);
    }

    const normalizedIndexMap: number[] = [];
    normalizedSegments.forEach((segment, rawIndex) => {
      const chars = Array.from(segment);
      chars.forEach(() => normalizedIndexMap.push(rawIndex));
    });

    type HighlightRange = { start: number; end: number; type: 'exact' | 'fuzzy' };
    const highlightRanges: HighlightRange[] = [];

    for (const term of terms) {
      const exactRanges = this.findExactHighlightRanges(term, normalizedText, normalizedIndexMap);
      if (exactRanges.length) {
        highlightRanges.push(...exactRanges);
        continue;
      }

      const fuzzyRange = this.findBestFuzzyHighlightRange(term, normalizedText, normalizedIndexMap);
      if (fuzzyRange) {
        highlightRanges.push(fuzzyRange);
      }
    }

    if (!highlightRanges.length) {
      return this.escapeHtml(text);
    }

    const mergedRanges = this.mergeHighlightRanges(highlightRanges);

    let cursor = 0;
    let html = '';
    for (const { start, end, type } of mergedRanges) {
      if (cursor < start) {
        html += this.escapeHtml(rawChars.slice(cursor, start).join(''));
      }
      html += `<mark class="floating-toc__highlight floating-toc__highlight--${type}" data-match="${type}">${this.escapeHtml(rawChars.slice(start, end).join(''))}</mark>`;
      cursor = end;
    }

    if (cursor < rawChars.length) {
      html += this.escapeHtml(rawChars.slice(cursor).join(''));
    }

    return html;
  }

  private findExactHighlightRanges(
    term: string,
    normalizedText: string,
    normalizedIndexMap: number[],
  ): Array<{ start: number; end: number; type: 'exact' }> {
    const ranges: Array<{ start: number; end: number; type: 'exact' }> = [];
    if (!term.length || !normalizedText.length) {
      return ranges;
    }

    let pointer = 0;
    while (pointer <= normalizedText.length - term.length) {
      const matchIndex = normalizedText.indexOf(term, pointer);
      if (matchIndex === -1) {
        break;
      }

      const normalizedEnd = matchIndex + term.length - 1;
      const rawStart = normalizedIndexMap[matchIndex];
      const rawEndIndex = normalizedIndexMap[normalizedEnd];

      if (rawStart !== undefined && rawEndIndex !== undefined) {
        ranges.push({ start: rawStart, end: rawEndIndex + 1, type: 'exact' });
      }

      pointer = matchIndex + term.length;
    }

    return ranges;
  }

  private findBestFuzzyHighlightRange(
    term: string,
    normalizedText: string,
    normalizedIndexMap: number[],
  ): { start: number; end: number; type: 'fuzzy' } | undefined {
    if (!term.length || !normalizedText.length) {
      return undefined;
    }

    const maxDistance = Math.max(1, Math.round(term.length * 0.2));
    let bestMatch:
      | {
          start: number;
          length: number;
          distance: number;
        }
      | undefined;

    const minWindow = Math.max(1, term.length - maxDistance);
    const maxWindow = Math.min(normalizedText.length, term.length + maxDistance);

    for (let len = minWindow; len <= maxWindow; len++) {
      for (let start = 0; start + len <= normalizedText.length; start++) {
        const candidate = normalizedText.slice(start, start + len);
        const distance = this.levenshteinDistance(term, candidate);

        if (distance > maxDistance) {
          continue;
        }

        if (!bestMatch || distance < bestMatch.distance || (distance === bestMatch.distance && len < bestMatch.length)) {
          bestMatch = { start, length: len, distance };
        }
      }
    }

    if (!bestMatch) {
      return undefined;
    }

    const rawStart = normalizedIndexMap[bestMatch.start];
    const rawEndIndex = normalizedIndexMap[bestMatch.start + bestMatch.length - 1];

    if (rawStart === undefined || rawEndIndex === undefined) {
      return undefined;
    }

    return {
      start: rawStart,
      end: rawEndIndex + 1,
      type: 'fuzzy',
    };
  }

  private mergeHighlightRanges(
    ranges: Array<{ start: number; end: number; type: 'exact' | 'fuzzy' }>,
  ): Array<{ start: number; end: number; type: 'exact' | 'fuzzy' }> {
    if (!ranges.length) {
      return [];
    }

    const sorted = [...ranges].sort((a, b) => a.start - b.start || a.end - b.end);
    const merged: Array<{ start: number; end: number; type: 'exact' | 'fuzzy' }> = [];

    for (const range of sorted) {
      const last = merged[merged.length - 1];
      if (!last) {
        merged.push({ ...range });
        continue;
      }

      if (range.start <= last.end) {
        last.end = Math.max(last.end, range.end);
        if (last.type !== 'exact' && range.type === 'exact') {
          last.type = 'exact';
        }
      } else {
        merged.push({ ...range });
      }
    }

    return merged;
  }

  private levenshteinDistance(a: string, b: string): number {
    if (a === b) {
      return 0;
    }

    if (!a.length) {
      return b.length;
    }
    if (!b.length) {
      return a.length;
    }

    const previousRow = new Array(b.length + 1);
    const currentRow = new Array(b.length + 1);

    for (let j = 0; j <= b.length; j++) {
      previousRow[j] = j;
    }

    for (let i = 1; i <= a.length; i++) {
      currentRow[0] = i;
      let previousDiagonal = i - 1;

      for (let j = 1; j <= b.length; j++) {
        const temp = previousRow[j];
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        currentRow[j] = Math.min(
          currentRow[j - 1] + 1,
          previousRow[j] + 1,
          previousDiagonal + cost,
        );
        previousDiagonal = temp;
      }

      for (let j = 0; j <= b.length; j++) {
        previousRow[j] = currentRow[j];
      }
    }

    return currentRow[b.length];
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private deferBlur(event: Event): void {
    const target = event.currentTarget as HTMLElement | null;
    if (target) {
      setTimeout(() => target.blur(), 0);
    }
  }
}
